/**
 * Initialize TP split fields from a raw Voluum path (no TP keys yet).
 *
 * Voluum does not echo TrafficPulse `__tp*` keys; without them we only know
 * `autoOptimized`. `ensurePathTpAiFlagsPresent` fills any missing split keys so a
 * partial payload (e.g. only offer/lander false) still pairs with path intent on PUT.
 */

import type { VoluumPathUpdate } from '@trafficpulse/shared';

import {
  baseCalculationMethod,
  readTpCalc,
  readTpFlag,
  TP_OFFERS_CALCULATION_METHOD_KEY,
  TP_OFFERS_LANDERS_AI_KEY,
  TP_PATHS_CALCULATION_METHOD_KEY,
  TP_PATHS_DISTRIBUTION_AI_KEY,
} from './path-ai-read';
import {
  syncPathAutoOptimized,
  syncPathCalculationMethod,
} from './path-ai-voluum-sync';

/**
 * Fill missing split flags. Per-path `autoOptimized` is the offer/lander
 * toggle in Voluum's contract (set-level Traffic Distribution AI lives on
 * `flow.automationConfiguration` and `group.automationConfiguration`). So we
 * mirror `autoOptimized` into `__tpOffersLandersAi` when it's not yet set —
 * otherwise a freshly loaded flow shows the switch off even when Voluum has
 * the optimization on.
 */
export function ensurePathTpAiFlagsPresent(p: VoluumPathUpdate): void {
  if (readTpFlag(p, TP_PATHS_DISTRIBUTION_AI_KEY) === undefined) {
    p[TP_PATHS_DISTRIBUTION_AI_KEY] = p.autoOptimized;
  }
  if (readTpFlag(p, TP_OFFERS_LANDERS_AI_KEY) === undefined) {
    p[TP_OFFERS_LANDERS_AI_KEY] = p.autoOptimized;
  }
}

export function hydrateSinglePath(p: VoluumPathUpdate): void {
  ensurePathTpAiFlagsPresent(p);
  syncPathAutoOptimized(p);

  const hasCalc =
    readTpCalc(p, TP_PATHS_CALCULATION_METHOD_KEY) !== undefined ||
    readTpCalc(p, TP_OFFERS_CALCULATION_METHOD_KEY) !== undefined;
  if (hasCalc) syncPathCalculationMethod(p);
  else {
    const b = baseCalculationMethod(p);
    p[TP_PATHS_CALCULATION_METHOD_KEY] = b;
    p[TP_OFFERS_CALCULATION_METHOD_KEY] = b;
    syncPathCalculationMethod(p);
  }
}
